import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, RotateCcw, Wifi, WifiOff, Smartphone, Volume2, VolumeX, ChevronRight, Captions } from 'lucide-react';
import { TOUR, DONE_CAPTION } from './tourScript';
import { STUDIO_VOICES } from './useNarrator';
import './frontline.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Phone remote for the guided tour. Public by code (unguessable, 3-hour sessions) so the presenter needn't sign in on the phone.
export default function TourRemotePage() {
  const { code } = useParams();
  const [st, setSt] = useState(null); // tour state from the big screen
  const [online, setOnline] = useState(false);
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState('');
  const seen = useRef(false);

  useEffect(() => {
    let dead = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/api/mirror/frontline/remote/sessions/${code}?role=phone`);
        if (r.status === 404) { if (!dead) setGone(true); return; }
        const d = await r.json();
        if (dead) return;
        seen.current = true; setSt(d.state); setOnline(!!d.screen_online);
      } catch { if (!dead) setOnline(false); }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => { dead = true; clearInterval(t); };
  }, [code]);

  const send = async (cmd) => {
    setBusy(cmd);
    if (navigator.vibrate) navigator.vibrate(20);
    try { await fetch(`${API}/api/mirror/frontline/remote/sessions/${code}/command`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cmd }) }); } catch { /* next poll shows the truth */ }
    setTimeout(() => setBusy(''), 250);
  };

  if (gone) {
    return (
      <main className="fl-remote" data-testid="fl-remote-gone">
        <Smartphone size={36} className="fl-muted" />
        <h1>This remote has expired</h1>
        <p className="fl-muted">Open the Guided tour on the big screen and tap “Remote” for a fresh code.</p>
      </main>
    );
  }
  const total = st?.total || TOUR.length;
  const step = st?.step ?? 0;
  const done = !!st?.done;
  const now = done ? { title: "That's the evidence loop", caption: DONE_CAPTION } : TOUR[Math.min(step, TOUR.length - 1)];
  const next = done ? null : step + 1 < TOUR.length ? TOUR[step + 1] : { title: "That's the evidence loop", caption: DONE_CAPTION, persona: '' };
  return (
    <main className="fl-remote" data-testid="fl-remote" data-step={step} data-playing={st?.playing ? '1' : '0'}>
      <p className="pie-eyebrow fl-remote-eyebrow"><Smartphone size={13} /> Mirror · Presenter remote</p>
      <p className={`fl-remote-status ${online ? 'fl-remote-on' : ''}`} data-testid="fl-remote-status">{online ? <><Wifi size={14} /> Connected to the big screen</> : <><WifiOff size={14} /> {seen.current ? 'Big screen not responding…' : 'Connecting…'}</>}</p>
      <section className="fl-remote-now" data-testid="fl-remote-now">
        <p className="fl-remote-count">{done ? 'Tour complete' : `Step ${step + 1} of ${total} · ${st?.persona === 'manager' ? 'Maya' : 'Alex'}`}</p>
        <h1 data-testid="fl-remote-title">{done ? "That's the evidence loop" : (st?.title || '…')}</h1>
        {st && <p className="fl-remote-notes" data-testid="fl-remote-notes">{now.caption}</p>}
        {st?.speaking && <p className="fl-remote-speaking" data-testid="fl-remote-speaking"><Volume2 size={13} /> Narrator still speaking — let it land before Next</p>}
      </section>
      {st && next && (
        <section className="fl-remote-next" data-testid="fl-remote-next-notes">
          <p className="fl-remote-count"><ChevronRight size={12} /> Up next{next.persona ? ` · ${next.persona === 'manager' ? 'Maya' : 'Alex'}` : ''}</p>
          <p className="fl-remote-next-title" data-testid="fl-remote-next-title">{next.title}</p>
          <p className="fl-remote-notes">{next.caption}</p>
        </section>
      )}
      <div className="fl-remote-grid">
        <button type="button" className="fl-remote-btn" onClick={() => send('prev')} disabled={step === 0 && !done} data-testid="fl-remote-prev"><SkipBack size={28} /><span>Back</span></button>
        <button type="button" className={`fl-remote-btn fl-remote-main ${busy === 'toggle' ? 'fl-remote-busy' : ''}`} onClick={() => send(done ? 'restart' : 'toggle')} data-testid="fl-remote-play">
          {done ? <><RotateCcw size={34} /><span>Replay</span></> : st?.playing ? <><Pause size={34} /><span>Pause</span></> : <><Play size={34} /><span>Play</span></>}
        </button>
        <button type="button" className="fl-remote-btn" onClick={() => send('next')} disabled={done} data-testid="fl-remote-next"><SkipForward size={28} /><span>Next</span></button>
      </div>
      <button type="button" className="fl-remote-link" onClick={() => send('restart')} data-testid="fl-remote-restart"><RotateCcw size={14} /> Restart from step 1</button>
      {st && (
        <section className="fl-remote-nar" data-testid="fl-remote-narrator" data-on={st.narrator ? '1' : '0'}>
          <div className="fl-remote-nar-head">
            <button type="button" className={`fl-remote-chip fl-remote-chip-main ${st.narrator ? 'fl-remote-chip-on' : ''}`} onClick={() => send('narrate_toggle')} aria-pressed={!!st.narrator} data-testid="fl-remote-narrate">{st.narrator ? <><Volume2 size={15} /> Narrator on</> : <><VolumeX size={15} /> Narrator muted</>}</button>
            <button type="button" className={`fl-remote-chip ${st.captions ? 'fl-remote-chip-on' : ''}`} onClick={() => send('captions_toggle')} aria-pressed={!!st.captions} data-testid="fl-remote-captions"><Captions size={15} /> Captions</button>
          </div>
          <p className="fl-remote-count" style={{ margin: '12px 0 6px' }}>Voice{st.narrator && st.voice_status ? ` · ${st.voice_status}` : ''}</p>
          <div className="fl-remote-voices">
            {STUDIO_VOICES.map(([v, l]) => <button key={v} type="button" className={`fl-remote-chip ${st.voice === v ? 'fl-remote-chip-on' : ''}`} onClick={() => send(`voice:${v}`)} aria-pressed={st.voice === v} data-testid={`fl-remote-voice-${v}`}>{l.split(' · ')[0]}</button>)}
          </div>
        </section>
      )}
      <p className="fl-small fl-remote-foot">Code {code} · expires with the session · nothing else on this phone is connected</p>
    </main>
  );
}
